import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Download,
  Loader2,
  MoreVertical,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import { useViewModelReady } from '../../../../hooks/useViewModel';
import { Tabs, TabDef } from '../../../../components/Tabs';
import { Campaign, CampaignTaskType } from '../../../../types';
import { getTaskTypeDescriptor, listTaskTypeDescriptors, TaskTypeDescriptor } from '../../../../taskTypes/descriptor';
import { CampaignProgressBar } from '../../../../components/CampaignProgressBar';
import { PortalMenu } from '../../../../components/PortalMenu';
import { SearchableMultiSelectDropdown } from '../../../../components/SearchableDropdown';
import { ExportTasksModal } from './ExportTasksModal';
import {
  CampaignCounts,
  ExportTasksOptions,
  useCampaignsToolViewModel,
} from './viewModel';
import { paths } from '../../../../routes/paths';

type CampaignTabId = 'active' | 'active-no-tasks' | 'archived';

export function CampaignsTool({ mobileNavTrigger }: { mobileNavTrigger?: React.ReactNode }) {
  const vm = useCampaignsToolViewModel();
  const {
    countsByCampaign,
    countsLoading,
    dispose,
    exporting,
    filteredCampaigns,
    initialize,
    loadCounts,
  } = vm;
  useViewModelReady(initialize);
  useEffect(() => () => dispose(), [dispose]);

  const [tab, setTab] = useState<CampaignTabId>('active');

  // Tab counts and the visible list both run off the filtered set — so
  // narrowing the filter updates the tab badges too, not just the rows
  // below. Active campaigns split into "has at least one task type" vs
  // "no task types bound yet"; the latter is its own tab so admins can
  // triage unconfigured campaigns without scrolling past the configured
  // ones.
  const { active, activeNoTasks, archived } = useMemo(() => {
    const a: Campaign[] = [];
    const n: Campaign[] = [];
    const z: Campaign[] = [];
    for (const c of filteredCampaigns) {
      if (c.archivedAt) {
        z.push(c);
      } else if ((c.taskTypes?.length ?? 0) === 0) {
        n.push(c);
      } else {
        a.push(c);
      }
    }
    return { active: a, activeNoTasks: n, archived: z };
  }, [filteredCampaigns]);
  const visibleCampaigns = (() => {
    if (tab === 'archived') return archived;
    if (tab === 'active-no-tasks') return activeNoTasks;
    return active;
  })();

  const hasFilter = vm.query.trim().length > 0 || vm.taskTypeIds.length > 0;
  const emptyStateCopy = (() => {
    if (hasFilter) return 'No campaigns match the current filters.';
    if (tab === 'archived') {
      return 'No archived campaigns. Campaigns appear here after Aligned drops them from its active set.';
    }
    if (tab === 'active-no-tasks') {
      return 'No active campaigns are missing a task type. Bind a task type to a campaign and it moves to Active.';
    }
    return 'No synced campaigns yet. Click "Sync campaigns" to pull the latest from Aligned.';
  })();

  useEffect(() => {
    for (const c of visibleCampaigns) {
      if (!countsByCampaign.has(c.id) && !countsLoading.has(c.id)) {
        void loadCounts(c.id);
      }
    }
  }, [visibleCampaigns, countsByCampaign, countsLoading, loadCounts]);

  const tabs: TabDef[] = [
    { id: 'active', label: 'Active', count: active.length },
    { id: 'active-no-tasks', label: 'Active - No Tasks', count: activeNoTasks.length },
    { id: 'archived', label: 'Archived', count: archived.length },
  ];

  return (
    <section className="min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Campaigns</h1>
          <p className="text-sm text-gray-500 mt-1">
            Synced snapshot of active campaigns in Aligned. Click a campaign to bind task types,
            tune target counts, and review the experts working on it.
          </p>
        </div>
        {mobileNavTrigger}
      </div>

      {vm.error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
          {vm.error}
        </div>
      )}

      {vm.busy && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {!vm.busy && (
        <>
          <CampaignsFilterBar
            query={vm.query}
            onQueryChange={vm.setQuery}
            taskTypeIds={vm.taskTypeIds}
            onToggleTaskType={vm.toggleTaskTypeFilter}
            onClearTaskTypes={vm.clearTaskTypeFilter}
            totalCount={vm.campaigns.length}
            filteredCount={vm.filteredCampaigns.length}
            syncing={vm.syncing}
            onSync={() => void vm.sync()}
          />

          {vm.lastSyncResult && (
            <p className="mt-2 text-xs text-gray-500">
              Last sync: {vm.lastSyncResult.campaignCount} campaigns (
              {vm.lastSyncResult.created} new, {vm.lastSyncResult.updated} updated,{' '}
              {vm.lastSyncResult.reactivated} reactivated, {vm.lastSyncResult.archived} archived),{' '}
              {vm.lastSyncResult.expertCount} active experts,{' '}
              {vm.lastSyncResult.reviewerCount ?? 0} Aligned reviewers (
              {vm.lastSyncResult.reviewerGrantsAdded ?? 0} review grants added,{' '}
              {vm.lastSyncResult.reviewerGrantsRemoved ?? 0} removed) at{' '}
              {new Date(vm.lastSyncResult.syncedAt).toLocaleString()}.
            </p>
          )}

          <div className="mt-6">
            <Tabs tabs={tabs} activeId={tab} onChange={(id) => setTab(id as CampaignTabId)} />
          </div>
          <div className="mt-4 bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {visibleCampaigns.length === 0 && (
              <div className="p-6 text-sm text-gray-500">{emptyStateCopy}</div>
            )}
            {visibleCampaigns.map((c) => (
              <CampaignRow
                key={c.id}
                campaign={c}
                usersByExpertIdSize={countMatchedExperts(c, vm.usersByExpertId)}
                counts={countsByCampaign.get(c.id) ?? null}
                countsLoading={countsLoading.has(c.id)}
                exporting={exporting.has(c.id)}
                onExport={(options) => vm.exportTasks(c.id, options)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function countMatchedExperts(
  campaign: Campaign,
  usersByExpertId: ReturnType<typeof useCampaignsToolViewModel>['usersByExpertId'],
): number {
  let matched = 0;
  for (const id of campaign.activeExpertIds) {
    if (usersByExpertId.has(id)) matched += 1;
  }
  return matched;
}

interface CampaignsFilterBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  taskTypeIds: string[];
  onToggleTaskType: (id: string) => void;
  onClearTaskTypes: () => void;
  totalCount: number;
  filteredCount: number;
  syncing: boolean;
  onSync: () => void;
}

/**
 * Filter bar for the Campaigns tab. Mirrors the shape of UsersTool's bar —
 * search + faceted dropdown + a trailing primary action (the Sync button
 * used to live in the section header; it belongs here now so all
 * campaign-wide controls share a row). Task-type filter is a dropdown
 * rather than chips because the number of registered task types grows over
 * time — a chip row would overflow the bar once we pass a handful of types.
 */
function CampaignsFilterBar({
  query,
  onQueryChange,
  taskTypeIds,
  onToggleTaskType,
  onClearTaskTypes,
  totalCount,
  filteredCount,
  syncing,
  onSync,
}: CampaignsFilterBarProps) {
  const hasFilter = query.trim().length > 0 || taskTypeIds.length > 0;

  return (
    <div className="mt-6 flex flex-wrap items-center gap-3">
      <div className="relative w-full sm:min-w-[240px] sm:max-w-md sm:flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search campaign name…"
          aria-label="Search campaigns by name"
          className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {query && (
          <button
            type="button"
            onClick={() => onQueryChange('')}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <TaskTypeFilterMenu
        selected={taskTypeIds}
        onToggle={onToggleTaskType}
        onClear={onClearTaskTypes}
      />

      <div className="w-full text-xs text-gray-500 sm:w-auto">
        {hasFilter ? `${filteredCount} of ${totalCount}` : `${totalCount} total`}
      </div>

      <button
        type="button"
        onClick={onSync}
        disabled={syncing}
        className="inline-flex min-h-[40px] w-full items-center justify-center gap-2 px-3 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap sm:ml-auto sm:w-auto"
      >
        {syncing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        )}
        {syncing ? 'Syncing…' : 'Sync campaigns'}
      </button>
    </div>
  );
}

interface TaskTypeFilterMenuProps {
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}

/**
 * Dropdown menu of registered task types for the campaigns filter bar.
 * Click-outside + Escape mirror `CampaignRowMenu` for consistency. Uses
 * checkboxes (multi-select, OR semantics) rather than chips because the
 * registered-task-type count is expected to grow beyond what fits inline.
 */
function TaskTypeFilterMenu({ selected, onToggle, onClear }: TaskTypeFilterMenuProps) {
  const modules = useMemo<TaskTypeDescriptor[]>(
    () => listTaskTypeDescriptors(),
    [],
  );

  const count = selected.length;
  const label = count === 0 ? 'Task types' : `Task types · ${count}`;

  return (
    <SearchableMultiSelectDropdown
      options={modules.map((module) => ({
        value: module.id,
        label: module.shortName,
        description: module.displayName,
        searchText: `${module.shortName} ${module.displayName} ${module.id}`,
      }))}
      values={selected}
      onChange={(nextValues) => {
        if (nextValues.length === 0) {
          if (selected.length > 0) onClear();
          return;
        }
        const changedModule = modules.find((module) => (
          nextValues.includes(module.id) !== selected.includes(module.id)
        ));
        if (changedModule) onToggle(changedModule.id);
      }}
      placeholder="Task types"
      searchPlaceholder="Search task types"
      noResultsText={modules.length === 0 ? 'No task types registered.' : 'No matching task types'}
      ariaLabel={label}
      selectionLabel={label}
      clearAllLabel="Clear selection"
      preserveOrder
      className="w-full sm:w-64"
      triggerClassName={count > 0 ? 'border-primary bg-primary/5 text-primary' : ''}
    />
  );
}

interface CampaignRowProps {
  campaign: Campaign;
  usersByExpertIdSize: number;
  counts: CampaignCounts | null;
  countsLoading: boolean;
  exporting: boolean;
  onExport: (options: ExportTasksOptions) => Promise<void>;
}

function describeCampaignTypes(taskTypes: CampaignTaskType[]): string {
  if (taskTypes.length === 0) return 'No task types set';
  if (taskTypes.length === 1) {
    const t = taskTypes[0];
    return `${getTaskTypeDescriptor(t.taskTypeId).shortName} · target ${t.targetCount}`;
  }
  const sum = taskTypes.reduce((n, t) => n + t.targetCount, 0);
  return `${taskTypes.length} task types · target ${sum}`;
}

function CampaignRow({
  campaign,
  usersByExpertIdSize,
  counts,
  countsLoading,
  exporting,
  onExport,
}: CampaignRowProps) {
  const navigate = useNavigate();
  const [exportOpen, setExportOpen] = useState(false);
  const detailHref = paths.adminCampaign(campaign.id);

  return (
    <div>
      <div className="flex items-stretch hover:bg-gray-50">
        <Link
          to={detailHref}
          className="group flex-1 min-w-0 flex items-center gap-3 p-4 sm:gap-4"
        >
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-gray-500" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <p className="font-medium text-gray-900 truncate flex items-center gap-2">
                {campaign.name || '(no name)'}
                {campaign.archivedAt && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded bg-gray-200 text-gray-700">
                    Archived
                  </span>
                )}
              </p>
              <div className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                <Users className="h-3.5 w-3.5" />
                {usersByExpertIdSize} of {campaign.activeExpertIds.length}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {describeCampaignTypes(campaign.taskTypes)}
            </p>
            <CampaignProgressBar counts={counts} loading={countsLoading} />
          </div>
        </Link>
        <div className="flex items-center pr-2">
          <CampaignRowMenu
            exporting={exporting}
            onOpenExport={() => setExportOpen(true)}
            onOpenDetail={() => navigate(detailHref)}
          />
        </div>
      </div>
      {exportOpen && (
        <ExportTasksModal
          campaignName={campaign.name || '(no name)'}
          campaignTaskTypes={campaign.taskTypes}
          exporting={exporting}
          onClose={() => setExportOpen(false)}
          onExport={onExport}
        />
      )}
    </div>
  );
}

interface CampaignRowMenuProps {
  exporting: boolean;
  onOpenExport: () => void;
  onOpenDetail: () => void;
}

function CampaignRowMenu({ exporting, onOpenExport, onOpenDetail }: CampaignRowMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Campaign actions"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={exporting}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {exporting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <MoreVertical className="h-4 w-4" />
        )}
      </button>
      {open && (
        <PortalMenu
          anchorRef={triggerRef}
          onRequestClose={() => setOpen(false)}
          role="menu"
          className="w-44 py-1"
        >
          <button
            type="button"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onOpenDetail();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <ChevronRight className="h-4 w-4" />
            Open details
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onOpenExport();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export tasks…
          </button>
        </PortalMenu>
      )}
    </div>
  );
}
